import type {
	Alignment,
	JustifyContent,
	MeshItemAlignment,
	UnitSize,
	Spx,
	KeywordLength,
	Position,
	AlignContent,
} from '@wix/thunderbolt-becky-types'
import { resolveSpxValue } from '../css'

export interface SizeToStringOptions {
	spx?: Spx
	vhScalingFactor?: string
}

export const unitSizeToString = (size: UnitSize, options?: SizeToStringOptions): string => {
	const { spx, vhScalingFactor } = options ?? {}

	if (size.type === 'percentage') {
		return `${size.value}%`
	}
	if (size.type === 'number') {
		return `${size.value}`
	}
	if (size.type === 'spx') {
		return resolveSpxValue(`${size.value}spx`, spx)
	}
	if (size.type === 'vh' && vhScalingFactor) {
		return vhValueToString(size.value, vhScalingFactor)
	}

	return `${size.value}${size.type}`
}

export const vhValueToString = (value: number, vhScalingFactor?: string) => {
	if (vhScalingFactor) {
		return `calc(${value} * var(--vh-scale-factor, 1vh))`
	}

	return `${value}vh`
}

export const numberToString = (number: number): string => number.toString()

export const gridAlignmentToString = (alignment: Alignment): string => alignment.toLowerCase()

// consider move all code related to content area to layoutToCss
//
export const meshAlignmentToString = (alignment: MeshItemAlignment): string => {
	if (alignment === 'content') {
		return 'start'
	}

	return alignment.toLowerCase()
}

const alignmentToFlexSyntax = {
	start: 'flex-start' as const,
	end: 'flex-end' as const,
	center: 'center' as const,
	stretch: 'stretch' as const,
	auto: 'auto' as const,
}

export const flexAlignmentToString = (alignment: Alignment): string => alignmentToFlexSyntax[alignment]

const contentAlignmentToFlexSyntax = {
	start: 'flex-start' as const,
	end: 'flex-end' as const,
	center: 'center' as const,
	spaceBetween: 'space-between' as const,
	spaceAround: 'space-around' as const,
	spaceEvenly: 'space-evenly' as const,
}

export const flexJustifyContentToString = (justifyContent: JustifyContent): string =>
	contentAlignmentToFlexSyntax[justifyContent]

export const flexAlignContentToString = (alignContent: AlignContent): string =>
	contentAlignmentToFlexSyntax[alignContent]

export const templateLengthToString = (value: number | KeywordLength): string => {
	if (typeof value === 'number') {
		return numberToString(value)
	}
	return value.toLowerCase()
}

export const positionToCss = (
	position: Position,
	renderSticky: boolean
): Record<string, string> => {
	const isStickyToHeader = position === 'stickyToHeader'
	const isStickyPosition = position === 'sticky' || isStickyToHeader
	const isStickyDisabled = isStickyPosition && !renderSticky

	if (isStickyDisabled) {
		return {
			position: 'relative',
			forceAuto: 'auto',
		}
	}

	return {
		position: isStickyToHeader ? 'sticky' : position,
		headerOffset: isStickyToHeader ? 'var(--top-offset)' : '0px',
		stickyOffset: isStickyPosition ? 'var(--sticky-offset)' : '0px',
		forceAuto: 'initial',
	}
}
